import i18next, { i18n, InitOptions as i18nInitOptions, Resource as i18nResource } from "i18next";
import ICU from "i18next-icu";
import i18nextXhrBackend from "i18next-xhr-backend";
import stripJsonComments from "strip-json-comments";
import ChainedBackend from "./ChainedBackend";
import { Backend } from "./types";
import { Currency } from "./utils/Currency";
export { Currency } from "./utils/Currency";

const DEFAULT_LANGUAGE = "en";

const DEFAULT_PSEUDOLANGUAGE = "pseudo";

const DEFAULT_NS = "translation";

const DEFAULT_CURRENCY = Currency.Code.USD;

const DEFAULT_TIMEZONE: string | undefined = undefined; // undefined defaults to OS timezone

const MAXIMUM_FRACTION_DIGITS = 4;

type tFnType = (key: string, args?: { [key: string]: string | number | Date }) => string;

export interface ResourceKey {
  [key: string]: any;
}

export interface Resource {
  [language: string]: ResourceKey;
}

export interface LocaleDatum {
  locale: string;
  parentLocale?: string;
  pluralRuleFunction: () => string;
  fields?: {
    [name: string]: {
      displayName: string;
      relative: object;
      relativeTime: object;
      past: object;
    };
  };
}

export type LocaleData = LocaleDatum[];

/**
 * Initialization options for instance of LoomI18n
 */
export interface InitOptions {
  /**
   * Language to use by default after initialization:
   * uses languageTags from ./utils/supportedLocales,ts
   */
  defaultLanguage?: string;
  /**
   * The string resources bundled with the instance. Bundled resources may still be remotely fetched,
   * but will be available even if the fetch operation fails.
   */
  resources?: Resource;
  /**
   * The remote resources patterns to use when resolving non-bundled resources. Fetch will be chained
   * in order, and the pattern to resolve will be used for the language. This enable getting resources
   * that may not be fully translated yet.
   */
  remoteResourcesPatterns?: string[];
  /**
   * The locale data (http://cldr.unicode.org/) to bundle in the instance. Locale data not bundle will be
   * attached to page and downloaded asynchronously when changing language.
   */
  localesData?: { [locale: string]: LocaleData[] };
  /**
   * The default currency to use. Accepted 3-letter Currency Codes from here:
   * https://www.currency-iso.org/dam/downloads/lists/list_one.xml
   */
  defaultCurrency?: Currency.Code;
  /**
   * The default timezone to use. Time zone names from:
   * https://twiki.org/cgi-bin/xtra/tzdatepick.html
   */
  defaultTimezone?: string;
}

/**
 * Base LoomI18n class. This class wraps an underlying library that does the actual translation
 * with support to ICU Message Format. Must be initialized before using.
 */
export class LoomI18n {
  private i18n: i18n;
  private icu: ICU;
  private bundledLocaleData: Set<string>;
  public isInitialized: boolean;
  private changeLanguageCallback: undefined | ((language: string, error?: any) => void);
  private initCallback: undefined | ((initLang: string, error?: any) => void);
  private missingKeyCallback: undefined | ((key: string) => void);

  public defaultCurrency = DEFAULT_CURRENCY;
  public defaultTimezone = DEFAULT_TIMEZONE;

  constructor() {
    this.isInitialized = false;
    this.i18n = i18next.createInstance();
    this.i18n.init();
  }

  /**
   * Initialize the instance. If no {@code initOptions} are provided, use sensible defaults.
   * @param initOptions The options to use when initializing the instance.
   * @returns Promise<void>
   */
  public async init(initOptions: InitOptions = {}): Promise<void> {
    if (initOptions.defaultLanguage === DEFAULT_PSEUDOLANGUAGE) {
      initOptions.defaultLanguage = DEFAULT_LANGUAGE;
      this.tFn = this.pseudoT;
    }

    initOptions.defaultCurrency && (this.defaultCurrency = initOptions.defaultCurrency);
    initOptions.defaultTimezone && (this.defaultTimezone = initOptions.defaultTimezone);
    // Create the instance that we will actually initialize. This allow calling init multiple times (if
    // user wants to re-initialize the instance), and allow us to initialize to a dummy instance in the
    // constructor above
    this.i18n = i18next.createInstance();

    // Setup the ICU (International Components for Unicode) message format
    // This is the module that support pluralization and formatting of numbers and dates.
    this.icu = new ICU({
      formats: {
        ...getDateTimeFormats(this.defaultTimezone),
        ...getCurrencyFormats(this.defaultCurrency),
      },
    });

    this.bundledLocaleData = new Set<string>();
    if (initOptions.localesData) {
      Object.keys(initOptions.localesData).forEach(locale => {
        this.icu.addLocaleData(initOptions.localesData![locale]);
        this.bundledLocaleData.add(locale);
      });
    }

    // If the default language is not found in the bundled locales data, we need
    // to load it the same way as if the language was changed.
    const defaultLanguage = initOptions.defaultLanguage || DEFAULT_LANGUAGE;
    if (!this.bundledLocaleData.has(defaultLanguage)) {
      await this.loadLocaleData(defaultLanguage);
    }
    this.i18n.use(this.icu);

    const bundledLanguages = Object.keys(initOptions.resources || {});

    const i18nInitOptions: i18nInitOptions = {
      lng: defaultLanguage,
      fallbackLng: [...bundledLanguages, DEFAULT_LANGUAGE],
      defaultNS: DEFAULT_NS,
      debug: false,
      lowerCaseLng: false,
      resources: adaptResources(initOptions.resources),
      saveMissing: true,
      // Adding a no-op missing key handler as we use the even onMissingKey instead.
      // If we don't set an handler here when saveMissing is true, then i18next will
      // try to save the missing key to the backend
      missingKeyHandler: () => undefined,
    };

    // Initialization is different depending if we have remote resources or not, so we need to branch on this.
    // The main difference is that when using remote file, we need to initialize without bundled resources first,
    // Then add the bundles. When using w/o remote resource, we initialize with the bundles directly
    if (initOptions.remoteResourcesPatterns) {
      return this.initWithBackend(initOptions, i18nInitOptions)
        .then(() => {
          this.isInitialized = true;
          this.initCallback && this.initCallback(i18nInitOptions.lng!);
        })
        .catch(error => {
          this.initCallback && this.initCallback(i18nInitOptions.lng!, error);
          throw error;
        });
    } else {
      return this.initWithBundle(initOptions, i18nInitOptions)
        .then(() => {
          this.isInitialized = true;
          this.initCallback && this.initCallback(i18nInitOptions.lng!);
        })
        .catch(error => {
          this.initCallback && this.initCallback(i18nInitOptions.lng!, error);
          throw error;
        });
    }
  }

  /**
   * Initialize the instance synchronously. Can only be used when not using remote resources feature.
   * If no {@code initOptions} are provided, use sensible defaults.
   * @param initOptions same as @see InitOptions, but without `remoteResourcesPatterns`
   */
  public initSync(initOptions: Pick<InitOptions, Exclude<keyof InitOptions, "remoteResourcesPatterns">> = {}): void {
    this.init(initOptions);
  }

  private async initWithBackend(initOptions: InitOptions, i18nInitOptions: i18nInitOptions): Promise<void> {
    // Prevent remote loading of resources already bundled
    i18nInitOptions.partialBundledLanguages = true;

    // Setup a backend chain to allow reading locale files from different location.
    // This allow delivering versioned and unversioned translation.
    // e.g. /locales/version/en.json -> /locales/en.json
    const backendChain = new ChainedBackend(
      initOptions.remoteResourcesPatterns!.map(remoteResourcesPattern => {
        return new i18nextXhrBackend(null, {
          loadPath: remoteResourcesPattern,
          parse: (data: string) => JSON.parse(stripJsonComments(data)),
        }) as Backend;
      }),
    );

    this.i18n.use(backendChain);

    return new Promise<void>((resolve, reject) => {
      this.i18n.init(i18nInitOptions, (err: any) => {
        // Only propagate the error if we do not have resources for the default language
        if (err && !initOptions.resources) {
          reject(err);
        }

        resolve();
      });
    });
  }

  private async initWithBundle(initOptions: InitOptions, i18nInitOptions: i18nInitOptions): Promise<void> {
    // Setting this flag allow init to be synchronous, which is ok in this case since we do not have remote
    // resources to fetch.
    i18nInitOptions.initImmediate = false;

    // Doing some Promise magic here to ensure that the init function below runs before the initWithBundle returns
    let res: () => void;
    let rej: (reason?: any) => void;
    const p = new Promise<void>((resolve, reject) => {
      res = resolve;
      rej = reject;
    });

    this.i18n.init(i18nInitOptions, (err: any) => {
      // Only propagate the error if we do not have resources for the default language
      if (err && (!initOptions.resources || !i18nInitOptions.lng || !initOptions.resources[i18nInitOptions.lng])) {
        rej(err);
      }

      res();
    });

    return p;
  }

  private loadLocaleData(language: string): Promise<void> {
    const languageCode = language.split("-")[0];
    const localeDataPromise = Promise.resolve();

    // If we are running inside a browser, we need to dynamically load the locale-data to get proper
    // localization of plurals, dates and times.
    if (window && window.document && window.document.createElement) {
      localeDataPromise.then(
        () =>
          new Promise<void>(resolve => {
            // Check if the language data was loaded on init
            if (this.bundledLocaleData.has(languageCode)) {
              return resolve();
            }

            // Check if we already loaded the locale data
            const reactIntlLocaleData = (window as any).ReactIntlLocaleData;
            if (reactIntlLocaleData && reactIntlLocaleData[languageCode]) {
              return resolve();
            }

            // Check if we already added the script to the page
            const id = `loomi18n-locale-data-${language}`;
            if (window.document.getElementById(id)) {
              return resolve();
            }
            const localeDataScript = window.document.createElement("script");
            localeDataScript.id = id;
            localeDataScript.async = true;
            localeDataScript.src = `https://locales.plugins.oci.oraclecloud.com/locale-data/${languageCode}.js`;
            localeDataScript.onload = () => {
              // We need to re-read the object from window here, as it may not have existed previously
              const reactIntlLocaleData = (window as any).ReactIntlLocaleData;
              if (reactIntlLocaleData && reactIntlLocaleData[languageCode]) {
                this.icu.addLocaleData((window as any).ReactIntlLocaleData[languageCode]);
              } else {
                console.warn(
                  `Loaded locale-data script for languageCode ${languageCode} did not provide corresponding locale-data`,
                );
              }
              return resolve();
            };
            localeDataScript.onerror = err => {
              // We should not crash if we cannot load locale-data
              console.warn(`Error while loading locale-data script for languageCode ${languageCode}:`, err);
              return resolve();
            };

            window.document.body.appendChild(localeDataScript);
          }),
      );
    }

    return localeDataPromise;
  }

  /**
   * Change the language of the current instance.
   * @param language New language to use. Can be either language code (e.g. "en"), or locale code (e.g. "en-US"))
   * @returns Promise<void>
   */
  public async changeLanguage(language: string): Promise<void> {
    let realLangauge: string = language;
    if (realLangauge === DEFAULT_PSEUDOLANGUAGE) {
      realLangauge = DEFAULT_LANGUAGE;
      this.tFn = this.pseudoT;
    } else {
      this.tFn = this.realT;
    }

    const localeDataPromise = this.loadLocaleData(realLangauge);

    const changeLanguagePromise = new Promise<void>(resolve => {
      this.i18n.changeLanguage(realLangauge, (err: any) => {
        // Only log and warning if language change failed, since it will fallback to default anyway
        if (err) {
          console.warn("changeLanguage", err);
        }

        resolve();
      });
    });

    return Promise.all([localeDataPromise, changeLanguagePromise])
      .then(() => {
        this.changeLanguageCallback && this.changeLanguageCallback(language);
      })
      .catch(([localeDataError, changeLanguageError]) => {
        if (localeDataError) {
          console.error("Could not load required locale-data", localeDataError);
        }
        if (changeLanguageError) {
          console.error("Error while trying to change language", changeLanguageError);
        }
        this.changeLanguageCallback &&
          this.changeLanguageCallback(realLangauge, localeDataError || changeLanguageError);
        throw localeDataError || changeLanguageError;
      });
  }

  /**
   * Add a notification callback on change of language. Useful for applications that may need
   * to do work once the language is changed (e.g. forceUpdate of the root component in react).
   * Do not call this method before you called @method init
   * @param callback
   */
  public onLanguageChanged(callback: (language: string, error?: any) => void): any {
    this.changeLanguageCallback = callback;
  }

  private realT: tFnType = (key, args) => {
    return this.i18n.t(key, args);
  };

  private pseudoT: tFnType = (key, args) => {
    const s = this.i18n.t(key, args).toLowerCase();
    // see https://www.w3.org/International/articles/article-text-size for a discussion
    // for the pseudo text length:
    const len = Math.ceil(s.length * 1.3) - s.length;
    const r = "eé".repeat(len).slice(len);
    return `[[${s} ${r}]]`;
  };

  private tFn: tFnType = this.realT;

  /**
   * Returns the string value associated with the provided key/arguments.
   *
   * @param key
   * @param args
   */
  public t: tFnType = (key, args) => {
    try {
      return this.tFn(key, args);
    } catch (error) {
      this.missingKeyCallback && this.missingKeyCallback(key);
      console.error(`Schema mismatch for Message with key ${key}:`, error);
      return `[${key}]`;
    }
  };

  /**
   * Add a notification callback on missing translation key. Useful for applications that may need to do
   * work once the translation key is missing in translation files.
   * Do not call this method before you called @method init
   * @param callback
   */
  public onMissingKey(callback: (key: string) => void): void {
    this.missingKeyCallback = callback;
    this.i18n.on("missingKey", (_: string[], __: string, key: string) => {
      callback(key);
    });
  }

  /**
   * If set before you call @method init this callback would be called.
   * @param callback
   */
  public onInit(callback: (initLang: string, error?: any) => void): void {
    this.initCallback = callback;
  }

  /**
   * Function to format currency.
   * @param value
   * @deprecated We will be removing "Code" and only have optional "Options" object where you can specify the currency
   * @param codeOrOptions Currency code to use, defaults to USD. Use getCurrencyCode to map string to Currency.Code enum
   * or Options object to set currencyCode(defaults to USD), displayFormat(Defaults to Symbol) and precision
   * @deprecated We will be removing "displayFormat" in the future. You can set this in the Options object
   * @param displayFormat Display as symbol or name. Eg. $5.00 or 5 US Dollars, Defaults to Symbol.
   * This is ignored if "Options" object is provided
   */
  public formatCurrency(
    value: number,
    codeOrOptions?: Currency.Code | Currency.Options,
    displayFormat?: Currency.DisplayFormat,
  ): string {
    let code;
    let format;
    let precision;

    // In this case codeOrOptions will be a currency code
    if (codeOrOptions === undefined || typeof codeOrOptions === "string") {
      code = codeOrOptions;
      format = displayFormat;
    }

    // In this case codeOrOptions will be options object
    if (typeof codeOrOptions === "object") {
      code = codeOrOptions.currencyCode;
      format = codeOrOptions.displayFormat;
      precision = codeOrOptions.precision;
    }

    const options: any = {};
    options.style = "currency";
    options.currency = code || this.defaultCurrency;
    options.currencyDisplay = format;
    if (precision) {
      options.maximumFractionDigits = MAXIMUM_FRACTION_DIGITS;
    }

    return new Intl.NumberFormat(this.i18n.language, options).format(value);
  }
}

function adaptResources(resources: Resource | undefined): i18nResource {
  const i18nextResource: i18nResource = {};

  if (resources) {
    Object.keys(resources).forEach(key => {
      i18nextResource[key] = {
        [DEFAULT_NS]: resources[key],
      };
    });
  }
  return i18nextResource;
}

function getCurrencyFormats(defaultCurrency: Currency.Code): any {
  // currency codes from https://www.currency-iso.org/dam/downloads/lists/list_one.xml
  const currencyCodes = Object.keys(Currency.Code);
  const currencySymbols = currencyCodes
    .map(v => {
      return { style: "currency", currency: v };
    })
    .reduce((obj: any, item: any) => {
      obj[item.currency] = item;
      return obj;
    }, {});
  const currencyNames = currencyCodes
    .map(v => {
      return { style: "currency", currency: v, currencyDisplay: "name" };
    })
    .reduce((obj: any, item: any) => {
      obj[`${item.currency}_name`] = item;
      return obj;
    }, {});
  return {
    number: {
      currency: {
        style: "currency",
        currency: defaultCurrency,
      },
      currency_name: {
        style: "currency",
        currency: defaultCurrency,
        currencyDisplay: "name",
      },
      ...currencySymbols,
      ...currencyNames,
    },
  };
}

/**
 * @param defaultTimezone User Time zone names from: https://twiki.org/cgi-bin/xtra/tzdatepick.html
 */
function getDateTimeFormats(defaultTimezone: string | undefined): any {
  const utcTimeZone = { hourCycle: "h23", timeZone: "UTC" };
  const userTimeZone = { timeZone: defaultTimezone };
  const shortDate = { month: "numeric", day: "numeric", year: "numeric" };
  const mediumDate = { month: "short", day: "numeric", year: "numeric" };
  const fullDate = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
  const shortTime = { hour: "numeric", minute: "numeric", timeZoneName: "short" };
  const fullTime = { hour: "numeric", minute: "numeric", second: "numeric", timeZoneName: "short" };

  return {
    time: {
      short: { ...shortTime, ...utcTimeZone },
      short_user: { ...shortTime, ...userTimeZone },
      medium: { ...shortTime, ...utcTimeZone },
      medium_user: { ...shortTime, ...userTimeZone },
      long: { ...fullTime, ...utcTimeZone },
      long_user: { ...fullTime, ...userTimeZone },
      full: { ...fullTime, ...utcTimeZone },
      full_user: { ...fullTime, ...userTimeZone },
    },
    date: {
      short: { ...shortDate, ...utcTimeZone },
      short_user: { ...shortDate, ...userTimeZone },
      medium: { ...mediumDate, ...utcTimeZone },
      medium_user: { ...mediumDate, ...userTimeZone },
      long: { ...mediumDate, ...utcTimeZone },
      long_user: { ...mediumDate, ...userTimeZone },
      full: { ...fullDate, ...utcTimeZone },
      full_user: { ...fullDate, ...userTimeZone },
      fullDateTime: { ...fullDate, ...fullTime, ...utcTimeZone },
      fullDateTime_user: { ...fullDate, ...fullTime, ...userTimeZone },
      longDateTime: { ...mediumDate, ...fullTime, ...utcTimeZone },
      longDateTime_user: { ...mediumDate, ...fullTime, ...userTimeZone },
      mediumDateTime: { ...mediumDate, ...shortTime, ...utcTimeZone },
      mediumDateTime_user: { ...mediumDate, ...shortTime, ...userTimeZone },
      shortDateTime: { ...shortDate, ...shortTime, ...utcTimeZone },
      shortDateTime_user: { ...shortDate, ...shortTime, ...userTimeZone },
    },
  };
}
